---
title: "Candidate table and kanban board"
description: "Switch between the candidate table and the kanban board in Chosen. Sort, reorder, and show or hide columns, including the built-in Date Applied column."
category: "candidates"
keywords:
  - "candidate table view"
  - "kanban board recruiting"
  - "candidate board view"
  - "sort candidate columns"
  - "collapse board column"
  - "candidate date applied"
schema: "HowTo"
nav:
  section: "guides"
  order: 10
  parent: "/guides/candidates"
---

Chosen shows your candidates two ways: a **table** and a **board**. The table is a spreadsheet you can sort and filter; the board is a kanban view grouped by status. Switch between them at the top of the candidate list — same candidates, different shape.

## Which view to use

Use the **table** when you want everyone at once and you're looking for patterns — every candidate who listed Python, everyone owned by a teammate, the whole pile sorted by [match score](/guides/candidates/match-rating). It's columns and rows, and you can sort or filter on any of them.

Use the **board** when you're moving people through stages. Each column is a [status](/guides/candidates/statuses-and-pipeline), each card is a candidate — name, current title and company, and a match score when there is one. Turn on the Date Applied column (below) and each card shows when they applied, too. The columns sit flush with a thin rule between them, so the row of stage headers reads as one pipeline. Good for a weekly review of who's stuck where.

Neither is the "real" view. They're the same data, and switching costs nothing.

## Switching views

<Steps>
  <li>Open a job's candidate list, or the master **Candidates** list from the left sidebar.</li>
  <li>Use the view toggle at the top of the list to switch between table and board.</li>
</Steps>

The view stays put while you move between jobs, but it isn't saved: a fresh visit opens the table.

## Collapsing board columns

Every stage header has a chevron that folds the column down to a narrow strip — the stage name turned sideways, plus a count. Click the strip's chevron to expand it back. Use it on the stages you're not working right now; the ones you are get the width.

Two things worth knowing:

- **A collapsed column still accepts drops.** You can drag a card onto the strip without expanding it first.
- **The layout is yours, per role.** Collapsing Lead on one role doesn't touch another role, and doesn't touch your teammates' boards. It's remembered in your browser.

On a role fed by marketplace recruiters, the traditional intake columns (Lead, Applied, Screening) start collapsed on their own when they're empty — marketplace candidates arrive as accepted submittals and never pass through them. A column opens itself the moment someone actually lands in it, and your own clicks always win over the default.

## Working with table columns

The table columns do what you'd expect from a spreadsheet:

- **Sort** — click a column header. Click again to reverse it. Sorting a column sorts the whole filtered list, not just the visible page.
- **Reorder** — drag a column header sideways to move it. Put the columns you check most on the left.
- **Resize** — drag the edge of a header. Widths persist, so a column you widened to read full job titles stays wide next time.

Which columns show up is yours to pick: the field manager (the settings control above the table) shows and hides any column, and adding a [custom field](/guides/candidates/custom-fields) adds a column you can then sort, move, and resize like any other.

<Callout type="info" title="Column layout is saved for you, not your whole org">
Column order, widths, and which columns show are stored as your own table preference. A teammate who reorders their columns doesn't move yours. There's no single shared layout — everyone tunes the table to how they read it.
</Callout>

### Date Applied

Every candidate has a built-in **Date Applied** column. It starts hidden. Turn it on in the field manager and it appears as the last column.

- **In a job's list**, it's the date the candidate applied to that job — or was added to it, whether by hand, by a recruiter, or from an import.
- **In the full Candidates list**, it's the date they first landed in Chosen.

So one person can carry two dates: when they first came in, and when they joined this particular role. Both are right.

The date comes from Chosen's own records, so it's read-only and already filled in for everyone, including candidates added long before the column existed. You can sort by it, it's included when you export to CSV, and turning it on also puts an "Applied Sep 3, 2026" line on each board card.

## Limits

The board groups strictly by status — it has no other grouping (by job, by owner, by source). For anything beyond status, use the table and its filters. And the board can get unwieldy once a status holds a few hundred candidates; at that size the table is the calmer place to work.
